/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  createAsyncThunk,
  createDraftSafeSelector,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { AxiosRequestConfig } from 'axios'
import apiClient from '../../../services/api-client'
import { RootState } from '../../../store/rootReducer'
import _ from 'lodash'
import {
  AccountsApiResponse,
  AuthState,
  DigitalFarmerAccountDictionary,
  AuthAccount,
  DigitalFarmerAccount,
  AtomicTokenApiResponse,
} from './types'

export const getAccounts = createAsyncThunk<AccountsApiResponse, void>('auth', async () => {
  const request = await apiClient.get<AccountsApiResponse, AxiosRequestConfig>('/accounts', {
    timeout: 60000,
  })
  return request.data
})

export const fetchAtomicToken = createAsyncThunk<AtomicTokenApiResponse, void>(
  'atomicToken',
  async () => {
    const request = await apiClient.post<AtomicTokenApiResponse, AxiosRequestConfig>(
      '/atomic/token',
      {
        timeout: 60000,
      },
    )
    return request.data
  },
)

export const initialState: AuthState = {
  accounts: {},
  token: null,
  atomicJWTToken: null,
  selectedAccountId: null,
}

const AuthSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  extraReducers(builder) {
    builder.addCase(getAccounts.fulfilled, (state, action) => {
      let _default = ''
      let hasAtleastOneShareholderAccount = false
      let hasAtleastOneTFOAccount = false

      let _proxyBlend: DigitalFarmerAccountDictionary = {}

      action.payload.accounts.forEach((acc) => {
        if (acc.isDefault) {
          _default = acc.accountNumber
        }

        if (acc.hasShareholderAccess) {
          hasAtleastOneShareholderAccount = true
        }

        if (acc.hasTfoAccess) {
          hasAtleastOneTFOAccount = true
        }

        _proxyBlend = {
          ..._proxyBlend,
          [acc.accountNumber]: {
            ...acc,
          },
        }
      })

      if (_default === '' && action.payload.accounts.length > 0) {
        _default = action.payload.accounts[0].accountNumber
      }

      const currentSelected =
        state.accounts[state.selectedAccountId as string]?.digitalFarmer?.selected ?? null

      const localData = state.accounts[state.selectedAccountId as string].digitalFarmer?.accounts

      if (localData) {
        const keys = Object.keys(localData)
        keys.forEach((key) => {
          if (_proxyBlend[key] && localData[key].lastViewedAt) {
            _proxyBlend[key].lastViewedAt = new Date(localData[key].lastViewedAt as Date)
          }
        })
      }

      delete state.accounts[state.selectedAccountId as string].digitalFarmer

      if (currentSelected) {
        if (_proxyBlend[currentSelected]) {
          _default = currentSelected
        }
      }

      const { digitalFarmer }: Pick<AuthAccount, 'digitalFarmer'> = {
        digitalFarmer: {
          contactFirstName: action.payload.contactFirstName,
          contactLastName: action.payload.contactLastName,
          accounts: _proxyBlend,
          selected: _default,
          isTfo: hasAtleastOneTFOAccount,
          hasShareholderAccounts: hasAtleastOneShareholderAccount,
        },
      }

      state.accounts[state.selectedAccountId as string].digitalFarmer = digitalFarmer
    })
    builder.addCase(fetchAtomicToken.fulfilled, (state, action) => {
      state.atomicJWTToken = action.payload.token
    })
  },
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
    },
    setSelectedAuthAccount: (state, action: PayloadAction<string>) => {
      state.selectedAccountId = action.payload
    },
    setSelectedFarmerAccount: (state, action: PayloadAction<string>) => {
      if (state.selectedAccountId && state.accounts[state.selectedAccountId].digitalFarmer) {
        state.accounts[state.selectedAccountId].digitalFarmer!.selected = action.payload
        state.accounts[state.selectedAccountId].digitalFarmer!.accounts[
          action.payload
        ].lastViewedAt = new Date()
      }
    },
    setAuthAccount: (state, action: PayloadAction<AuthAccount>) => {
      state.accounts = _.merge(state.accounts, {
        [action.payload.homeAccountId]: {
          ...action.payload,
        },
      })
      AuthSlice.caseReducers.setSelectedAuthAccount(
        state,
        AuthSlice.actions.setSelectedAuthAccount(action.payload.homeAccountId),
      )
    },
    addAuthAccount: (state, action: PayloadAction<AuthAccount>) => {
      state.accounts = _.merge(state.accounts, {
        [action.payload.homeAccountId]: {
          ...action.payload,
        },
      })
    },
    addAuthAccounts: (state, action: PayloadAction<AuthAccount[]>) => {
      let accounts = {}
      action.payload.forEach((acc) => {
        accounts = {
          ...accounts,
          [acc.homeAccountId]: {
            ...acc,
          },
        }
      })
      const __ = _.merge(accounts, state.accounts)
      state.accounts = __
    },
  },
})

const selectSelf = (state: RootState) => state.auth

const selectCurrenetAccountClaims = (state: RootState) =>
  state.auth.accounts[state.auth.selectedAccountId as string].idTokenClaims

export const selectEmailForPrefill = createSelector(selectSelf, (state) => {
  const digifarmer = state.accounts[state.selectedAccountId as string].digitalFarmer
  if (digifarmer && digifarmer.accounts[digifarmer.selected].hasTfoAccess) {
    return digifarmer.accounts[digifarmer.selected].contact?.email
  } else {
    const claims = state.accounts[state.selectedAccountId as string].idTokenClaims
    return claims?.emails ?? claims?.username
  }
})

export const selectNameForPrefill = createSelector(selectSelf, (state) => {
  const digifarmer = state.accounts[state.selectedAccountId as string].digitalFarmer
  if (digifarmer && digifarmer.accounts[digifarmer.selected].hasTfoAccess) {
    return digifarmer.accounts[digifarmer.selected].contact?.name
  } else {
    const claims = state.accounts[state.selectedAccountId as string].idTokenClaims
    return `${claims?.given_name} ${claims?.family_name}`
  }
})

export const selectPhoneForPrefill = createSelector(selectSelf, (state) => {
  const digifarmer = state.accounts[state.selectedAccountId as string].digitalFarmer
  if (digifarmer && digifarmer.accounts[digifarmer.selected].hasTfoAccess) {
    return digifarmer.accounts[digifarmer.selected].contact?.phone
  } else {
    return null
  }
})

export const selectName = createSelector(
  selectCurrenetAccountClaims,
  (state) => `${state.given_name} ${state.family_name}`,
)

export const selectEmail = createSelector(
  selectCurrenetAccountClaims,
  (state) => `${state.emails ?? state.username}`,
)

export const selectCurrentAccountId = createDraftSafeSelector(
  selectSelf,
  (state) => state.selectedAccountId,
)
export const selectToken = createSelector(selectSelf, (state) => state.token)
export const selectAtomicToken = createSelector(selectSelf, (state) => state.atomicJWTToken)

export const { addAuthAccount, addAuthAccounts, setAuthAccount, setSelectedAuthAccount } =
  AuthSlice.actions

export const selectAllFarmerAccounts = createSelector(
  selectSelf,
  (state) => state.accounts[state.selectedAccountId as string]?.digitalFarmer,
)

export const selectCurrentFarmerAccount = createDraftSafeSelector(selectSelf, (state) => {
  if (state.selectedAccountId && state.accounts[state.selectedAccountId]) {
    const acc = state.accounts[state.selectedAccountId]?.digitalFarmer
    if (acc) {
      return acc.accounts[acc.selected]
    }
  }

  return undefined
})

export const getProperty = (state: RootState) => {
  if (state.auth.selectedAccountId) {
    const accountId = state.auth.selectedAccountId
    const propertyId = state.auth.accounts[accountId].digitalFarmer?.selected
    return propertyId
  }
}

export const selectContactInfo = createDraftSafeSelector(selectCurrentFarmerAccount, (state) => {
  let name: string | undefined = undefined

  if (state?.relationshipOwner?.email) {
    if (state.relationshipOwner?.fullName) {
      name = `${state.relationshipOwner?.fullName}`.trim()
    }

    return {
      fullName: name,
      email: state.relationshipOwner?.email,
      phone: undefined,
    }
  }

  return {
    email: 'ask@farmlands.co.nz',
    phone: '0800 200 600',
  }
})

export const isMultiAccount = createSelector(selectCurrentFarmerAccount, (farmerAccounts) => {
  if (farmerAccounts) return Object.keys(farmerAccounts).length > 1

  return false
})

export const selectCanUseQuickOrder = createDraftSafeSelector(
  selectCurrentFarmerAccount,
  (farmerAccounts) => {
    return farmerAccounts?.hasCheckoutAccess
  },
)

export const selectHomeBranch = createDraftSafeSelector(
  selectCurrentFarmerAccount,
  (state: DigitalFarmerAccount | undefined): string | undefined => {
    return state?.homeBranchId
  },
)

export default AuthSlice
